import { IndicatorData } from '../TechnicalAnalysisTradingView';

export interface OhlcRow {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

// Net foreign buy/sell volume per day (positive = foreign net buying / domestic net selling,
// negative = foreign net selling / domestic net buying — domestic is the mirror of foreign).
export function calculateForeignDomestic(data: OhlcRow[], foreignFlowData: any[]): IndicatorData[] {
  const result: IndicatorData[] = [];

  if (!data || data.length === 0 || !foreignFlowData || foreignFlowData.length === 0) {
    return result;
  }

  // 1. Parse and sort foreign flow data chronologically by date string (YYYY-MM-DD)
  // Divide by 100 to convert shares to lots as requested.
  const sortedFlow = [...foreignFlowData]
    .map(row => ({
      dateStr: String(row.Date || row.date || '').slice(0, 10),
      netBuy: Number(row.NetBuyVol ?? row.netBuy ?? 0) / 100
    }))
    .filter(item => item.dateStr.length === 10)
    .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

  if (sortedFlow.length === 0) {
    return result;
  }

  // 2. Compute cumulative flow (running sum)
  let runningSum = 0;
  const flowCumulative = sortedFlow.map(item => {
    runningSum += item.netBuy;
    return {
      dateStr: item.dateStr,
      cumulativeValue: runningSum
    };
  });

  // 3. Map candle times to date strings and intervals
  const dataWithDates = data.map((candle, idx) => {
    const nextCandle = data[idx + 1];
    return {
      time: candle.time,
      dateStr: new Date(candle.time * 1000).toISOString().slice(0, 10),
      nextDateStr: nextCandle ? new Date(nextCandle.time * 1000).toISOString().slice(0, 10) : '9999-12-31'
    };
  });

  // 4. Map flow cumulative values to candles (supporting aggregation and carry-forward)
  let flowIdx = 0;
  let lastKnownCumulative = 0;
  let hasFoundFirstFlow = false;

  dataWithDates.forEach(candle => {
    let bestMatchIdx = -1;

    // Advance flowIdx to catch up to the current candle's dateStr
    while (flowIdx < flowCumulative.length && (flowCumulative[flowIdx]?.dateStr ?? '') < candle.dateStr) {
      lastKnownCumulative = flowCumulative[flowIdx]?.cumulativeValue ?? 0;
      hasFoundFirstFlow = true;
      flowIdx++;
    }

    // Examine flow records that belong to this candle's interval
    let tempIdx = flowIdx;
    while (tempIdx < flowCumulative.length && (flowCumulative[tempIdx]?.dateStr ?? '') < candle.nextDateStr) {
      bestMatchIdx = tempIdx;
      tempIdx++;
    }

    if (bestMatchIdx !== -1) {
      lastKnownCumulative = flowCumulative[bestMatchIdx]?.cumulativeValue ?? 0;
      hasFoundFirstFlow = true;
      flowIdx = tempIdx;
      result.push({ time: candle.time, value: lastKnownCumulative });
    } else if (hasFoundFirstFlow) {
      // Carry forward the last known cumulative value
      result.push({ time: candle.time, value: lastKnownCumulative });
    }
  });

  return result;
}
